import React, { useEffect, useState } from 'react';
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  BUTTON_SAVE,
  BUTTON_DANGER,
  BUTTON_WARNING,
  COLOR_INFO_TEXT,
} from '../theme/colors';

export type LgmButtonVariant = 'contained' | 'flat' | 'outlined';
export type LgmButtonColor =
  'primary' | 'secondary' | 'save' | 'danger' | 'warning';

export type LgmButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: LgmButtonVariant;
  color?: LgmButtonColor;
  hoverStyle?: React.CSSProperties;
  pressedStyle?: React.CSSProperties;
  focusStyle?: React.CSSProperties;
};

/**
 * LgmButton — the project's standard button primitive.
 *
 * Chosen via the Task 5 bake-off: an event-driven inline-style button rather
 * than an MUI-based one, so the lowest-level button carries **no UI-library
 * dependency** and stays within the project's inline-styles convention.
 * Because inline styles cannot express `:hover`/`:active`/`:focus-visible`,
 * those states are tracked explicitly via event handlers (the dominant
 * codebase pattern, e.g. TableModelCell.tsx, abstracted here).
 *
 * Accessibility: renders a native `<button>`, so it is focusable and handles
 * Enter/Space activation for free. `onKeyDown`/`onKeyUp` add the *pressed*
 * visual for keyboard activation, which inline styles can't get from `:active`.
 * The focus ring is shown only for keyboard focus (gated on `:focus-visible`),
 * so a mouse click doesn't leave a lingering outline — matching native behavior.
 *
 * Overridable: any `style` the caller passes is merged over the variant's
 * resting/base look, so font, size, padding, and a per-instance resting color
 * can be overridden per use. All native button attributes (`onClick`,
 * `disabled`, `type`, `aria-*`, `data-testid`, …) pass through. The
 * interaction handlers (`onMouseEnter`/`onMouseLeave`, `onMouseDown`/
 * `onMouseUp`, `onFocus`/`onBlur`, `onKeyDown`/`onKeyUp`) are **composed**: a
 * handler supplied via props runs first, then the internal hover/pressed/
 * focus state update — so consumers can attach behavior without losing the
 * visual states.
 *
 * Variants: `contained` (default, filled — the original bake-off style),
 * `flat` (transparent background, subtle tinted hover/pressed — for
 * lower-emphasis actions), and `outlined` (transparent background with a
 * border — for secondary actions next to a contained primary). Per-state
 * `hoverStyle`/`pressedStyle`/`focusStyle` let a caller override just the
 * interactive-state styling without re-implementing the whole component.
 *
 * Precedence: base < variant base < `style` (per-instance resting look) <
 * active interactive-state overlay (`hover`/`pressed`/`focus`/`disabled`,
 * merged with the matching `*Style` override). Interactive states always
 * render on top of `style` — e.g. a caller can set a custom resting
 * `backgroundColor` via `style` and still see `hoverStyle`'s color take over
 * on hover, matching how `:hover` overrides a base CSS rule.
 */
const LgmButton = ({
  children,
  variant = 'contained',
  color = 'primary',
  style,
  hoverStyle,
  pressedStyle,
  focusStyle,
  disabled,
  onClick,
  type = 'button',
  onMouseEnter,
  onMouseLeave,
  onMouseDown,
  onMouseUp,
  onFocus,
  onBlur,
  onKeyDown,
  onKeyUp,
  ...rest
}: LgmButtonProps) => {
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const [pressed, setPressed] = useState(false);

  // When the button becomes disabled, clear any lingering interaction state so
  // a stale hover/pressed/focus visual doesn't re-apply the moment it's re-enabled.
  useEffect(() => {
    if (disabled) {
      setHovered(false);
      setFocused(false);
      setPressed(false);
    }
  }, [disabled]);

  const colorRamp = getColorRamp(color);
  const variantStyles: VariantStyleSet = getVariantStyles(variant, colorRamp);

  const resolvedStyle: React.CSSProperties = {
    ...styles.base,
    ...variantStyles.base,
    ...style, // caller's resting-state override — font, size, padding, a per-instance base color, etc.
    ...(hovered && !disabled ? { ...variantStyles.hover, ...hoverStyle } : {}),
    ...(pressed && !disabled
      ? { ...variantStyles.pressed, ...pressedStyle }
      : {}),
    ...(focused && !disabled ? { ...styles.focus, ...focusStyle } : {}),
    ...(disabled ? variantStyles.disabled : {}),
  };

  const setPressedIfActivationKey = (
    e: React.KeyboardEvent<HTMLButtonElement>,
    value: boolean
  ) => {
    if (e.key === ' ' || e.key === 'Enter') setPressed(value);
  };

  return (
    <button
      {...rest}
      type={type}
      style={resolvedStyle}
      disabled={disabled}
      onClick={onClick}
      onMouseEnter={e => {
        onMouseEnter?.(e);
        setHovered(true);
      }}
      onMouseLeave={e => {
        onMouseLeave?.(e);
        setHovered(false);
        setPressed(false);
      }}
      onMouseDown={e => {
        onMouseDown?.(e);
        setPressed(true);
      }}
      onMouseUp={e => {
        onMouseUp?.(e);
        setPressed(false);
      }}
      onFocus={e => {
        onFocus?.(e);
        // Show the focus ring only for keyboard focus (:focus-visible), not mouse
        // clicks — matches native browser behavior so the outline doesn't linger
        // after a click. Fall back to showing it if the engine can't evaluate the
        // selector (e.g. older jsdom in unit tests).
        let focusVisible = true;
        try {
          focusVisible = e.currentTarget.matches(':focus-visible');
        } catch {
          focusVisible = true;
        }
        setFocused(focusVisible);
      }}
      onBlur={e => {
        onBlur?.(e);
        setFocused(false);
        setPressed(false);
      }}
      onKeyDown={e => {
        onKeyDown?.(e);
        setPressedIfActivationKey(e, true);
      }}
      onKeyUp={e => {
        onKeyUp?.(e);
        setPressedIfActivationKey(e, false);
      }}>
      {children}
    </button>
  );
};

type VariantStyleSet = {
  base: React.CSSProperties;
  hover: React.CSSProperties;
  pressed: React.CSSProperties;
  disabled: React.CSSProperties;
};

type ColorRamp = {
  rest: string;
  hover: string;
  pressed: string;
  contrastText: string;
  disabledBg: string;
  disabledText: string;
};

const getColorRamp = (color: LgmButtonColor): ColorRamp => {
  switch (color) {
    case 'secondary':
      return BUTTON_SECONDARY;
    case 'save':
      return BUTTON_SAVE;
    case 'danger':
      return BUTTON_DANGER;
    case 'warning':
      return BUTTON_WARNING;
    case 'primary':
    default:
      return BUTTON_PRIMARY;
  }
};

const getVariantStyles = (
  variant: LgmButtonVariant,
  ramp: ColorRamp
): VariantStyleSet => {
  switch (variant) {
    case 'flat':
      return {
        base: {
          color: ramp.rest,
          backgroundColor: 'transparent',
        },
        hover: {
          backgroundColor: `rgba(${hexToRgb(ramp.rest)}, 0.08)`,
          color: ramp.hover,
        },
        pressed: {
          backgroundColor: `rgba(${hexToRgb(ramp.rest)}, 0.16)`,
          color: ramp.pressed,
        },
        disabled: {
          // Borderless/text-only shapes always use the neutral muted gray,
          // not the role's own disabledBg/disabledText pair — those are
          // paired for a filled background and can be unreadable on transparent.
          color: BUTTON_SECONDARY.rest,
          backgroundColor: 'transparent',
          cursor: 'not-allowed',
        },
      };
    case 'outlined':
      return {
        base: {
          color: ramp.rest,
          backgroundColor: 'transparent',
          border: `1px solid ${ramp.rest}`,
          padding: 'calc(0.5rem - 1px) calc(1rem - 1px)',
        },
        hover: { backgroundColor: `rgba(${hexToRgb(ramp.rest)}, 0.08)` },
        pressed: { backgroundColor: `rgba(${hexToRgb(ramp.rest)}, 0.16)` },
        disabled: {
          color: BUTTON_SECONDARY.rest,
          backgroundColor: 'transparent',
          border: `1px solid ${BUTTON_SECONDARY.rest}`,
          cursor: 'not-allowed',
        },
      };
    case 'contained':
    default:
      return {
        base: {
          color: ramp.contrastText,
          backgroundColor: ramp.rest,
        },
        hover: { backgroundColor: ramp.hover },
        pressed: { backgroundColor: ramp.pressed },
        disabled: {
          backgroundColor: ramp.disabledBg,
          color: ramp.disabledText,
          cursor: 'not-allowed',
        },
      };
  }
};

const hexToRgb = (hex: string): string => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result) return '0, 0, 0';
  return `${parseInt(result[1], 16)}, ${parseInt(result[2], 16)}, ${parseInt(
    result[3],
    16
  )}`;
};

const styles: {
  base: React.CSSProperties;
  focus: React.CSSProperties;
} = {
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.375rem',
    fontFamily: 'inherit',
    fontWeight: 600,
    fontSize: '0.875rem', // rem so it scales with accessibility text-size settings
    border: 'none',
    borderRadius: '4px',
    padding: '0.5rem 1rem',
    cursor: 'pointer',
    transition:
      'background-color 120ms ease, color 120ms ease, border-color 120ms ease, box-shadow 120ms ease',
  },
  focus: {
    outline: `2px solid ${COLOR_INFO_TEXT}`,
    outlineOffset: '2px',
  },
};

export default LgmButton;
